import {Component, inject, OnInit, Provider, ViewChild} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {
  Currency,
  EventType,
  Flow,
  Location,
  Partner,
  Project,
  Service,
  ServiceCreate,
  ServiceType,
  ToastConfig,
  nextTempEventTypeId
} from '@app/shared';
import {NZ_DRAWER_DATA, NzDrawerRef} from 'ng-zorro-antd/drawer';
import {ProjectUtilsService} from '../../services/project-utils.service';
import {NzNotificationService} from 'ng-zorro-antd/notification';
import {
  compareById,
  deepClean,
  deepClone,
  disabledEndDate,
  disabledStartDate,
  FlowEventService,
  StServiceService
} from '@app/core';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {lastValueFrom} from 'rxjs';
import {GlobalValuesService} from '../../../../core/services/global-values.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ServicePricing, Unit} from '../../../../shared/models/service-pricing';


@Component({
  selector: 'app-create-service-drawer',
  standalone: false,

  templateUrl: './create-service-drawer.component.html',
  styleUrl: './create-service-drawer.component.less'
})
export class CreateServiceDrawerComponent implements OnInit {

  nzData: { project: Project } = inject(NZ_DRAWER_DATA);

  readonly totalPlannedMax = 2147483647;
  readonly totalPlannedMaxLabel = '2.147.483.647';

  step: 1 | 2 | 3 = 1;

  serviceForm!: FormGroup;
  flowsList: Flow[] = [];
  disabledEndDate = disabledEndDate;
  disabledStartDate = disabledStartDate;
  deepClone = deepClone;
  deepClean = deepClean;

  toastConfig: ToastConfig = {
    icon: 'check_circle',
    iconColor: '#6CDA75',
    content: 'Servicio creado.',
    showUndo: false
  };

  documentTypes: { name: string, id: number }[] = [];

  serviceTypes: ServiceType [] = [];

  notRef: any;
  @ViewChild('notificationTmpl') notificationTmpl: any;

  eventForm: FormGroup | undefined;
  activatorForm: FormGroup | undefined;
  selectedEvent: EventType | undefined;
  isFirstTime = true;
  locations: Location[] = []
  currencies:Currency[] = []
  pricingUnits = Object.values(Unit);

  partnerlist: Partner[] = [];
  clientsList: Partner[] = [];
  forwarderList: Partner[] = [];
  providerList: Partner[] = [];
  origenContactList: Partner[] = [];
  destinyContactList: Partner[] = [];

  activadorNameList = ['Subida documento', 'Validacion documento', 'Fecha Mojito', 'Manual'];

  creatingService: number = 0;

  constructor(private drawerRef: NzDrawerRef<string>, private fb: FormBuilder, public ps: ProjectUtilsService,
              private notificationService: NzNotificationService, private stServiceService: StServiceService,
              private flowEventService: FlowEventService,
              public globalValuesService: GlobalValuesService) {

    this.globalValuesService.flows$.pipe(takeUntilDestroyed())
      .subscribe((value) => this.flowsList = deepClone(value));
    this.globalValuesService.places$.pipe(takeUntilDestroyed())
      .subscribe((value) => {
        this.locations = value
      })
    this.globalValuesService.documentTypes$.pipe(takeUntilDestroyed())
      .subscribe((value) => {
        this.documentTypes = value
      })
    this.globalValuesService.isPartnerReady$.pipe(takeUntilDestroyed())
      .subscribe((value) => {
        if (value) {
          this.providerList=this.globalValuesService.providerList
          this.forwarderList = this.globalValuesService.forwarderList
          this.clientsList = this.globalValuesService.clientsList
          this.origenContactList = this.globalValuesService.origenContactList
          this.destinyContactList = this.globalValuesService.destinyContactList
        }
      })
    this.globalValuesService.serviceTypes$.pipe(takeUntilDestroyed())
      .subscribe((value) => {
        this.serviceTypes = value
      })
    this.globalValuesService.currencies$.pipe(takeUntilDestroyed())
      .subscribe((value) => {
        this.currencies = value
      })


  }

  get providers():Partner[]{
    return this.globalValuesService.providerList
  }

  get events(): FormArray {
    return this.serviceForm.get('eventTypes') as FormArray;
  }

  compareById = compareById;

  ngOnInit(): void {
    this.initForms();
  }

  openAddOrEditEvent(eventId: number | null): void {
    this.selectedEvent = eventId ? this.events.value.find((e: EventType) => e.id === eventId) : undefined;

    this.activatorForm = this.fb.group({
      name: [this.selectedEvent?.activator?.name || this.activadorNameList[0], Validators.required],
      value: [this.selectedEvent?.activator?.value, Validators.required],
      location: [this.selectedEvent?.activator?.location || null],
    });

    this.eventForm = this.fb.group({
      id: [this.selectedEvent?.id || nextTempEventTypeId()],
      name: [this.selectedEvent?.name || '', Validators.required],
      sequence: [this.selectedEvent?.sequence || this.events.length + 1, Validators.required],
      icon: [this.selectedEvent?.icon || '', Validators.required],
      color: [this.selectedEvent?.color || '', Validators.required],
      activator: this.activatorForm,
    })

    this.step = 3;
  }

  confirmAddOrEditEvent(): void {
    if (this.selectedEvent) {
      const index = this.events.value.findIndex(
        (control: any) => control.id === this.selectedEvent?.id
      );

      // Actualizar el FormControl en el FormArray
      this.events.at(index).patchValue({
        ...this.selectedEvent,
        ...this.eventForm?.value
      });
    } else {
      const currentLength = this.events!.length;
      const insertIndex = Math.max(0, currentLength - 1);

      // 1. Configurar sequence del NUEVO evento (penúltimo)
      this.eventForm!.get('sequence')?.setValue(currentLength); // Usamos currentLength, no +1

      // 2. Insertar en penúltima posición
      this.events!.insert(insertIndex, this.eventForm!);

      // 3. Actualizar el ULTIMO elemento (que ahora está desplazado)
      if (currentLength > 0) {
        const lastIndex = this.events!.length - 1;
        const lastControl = this.events!.at(lastIndex);
        lastControl.get('sequence')?.setValue(currentLength + 1);
      }
    }
    this.selectedEvent = undefined;
    this.step = 2;
  }

  selectFlow(flow: Flow): void {
    if (this.isFirstTime) {
      this.isFirstTime = false;
      this.serviceForm.get('flow')?.setValue(this.flowsList[0]);
      this.setEvents(flow);
      return;
    }
    this.setEvents(flow);
  }


  setEvents(flow: Flow) {
    this.flowEventService.setEvents(flow, this.events, this.fb);
  }

  async createService(close: boolean): Promise<void> {
    this.creatingService = close ? 1 : 2;

    this.serviceForm.value.flow.eventTypes = this.events.value;
    delete this.serviceForm.value.eventTypes;

    // Si el proyecto no tiene una lista definida se crea una vacia antes de asignarle el servicio
    if (!this.nzData.project.services) {
      this.nzData.project.services = [];
    }

    // Se crea la nueva entidad de Service
    const newService = await this.postService();

    if (newService) {
      // Si el servicio se crea correctamente se agrega al proyecto respectivo para una actualizacion sin peticiones por parte del front
      this.nzData.project.services.push(newService);

      // Se inicia el proceso de creacion de las entidades relacionadas con los eventos
      const isFlowFinished = await this.createEventsEntities(newService.id);

      // Si hay algun problema creando los eventos se cambia la configuracion del toast para dar feedback
      if (!isFlowFinished) {
        this.toastConfig = {
          icon: 'warning',
          iconColor: '#A35200',
          content: 'Se creó el servicio pero algo falló en el flujo o los eventos.',
          showUndo: false
        };
      }
    } else {
      // Si el servicio no se crea correctamente se cambia la configuracion del toast para dar feedback
      this.toastConfig = {
        icon: 'dangerous',
        iconColor: '#CB2A2F',
        content: 'Error al crear el proyecto.',
        showUndo: false
      };
    }

    this.notRef = this.notificationService
      .create("blank", '', this.notificationTmpl, {nzPlacement: "bottomRight"});

    this.creatingService = 0;

    if (close) {
      this.close();
    } else {
      this.clear();
      this.initForms();
    }
  }

  close(): void {
    this.clear();
    this.drawerRef.close();
  }

  clear(): void {
    this.step = 1;
    this.flowsList = [];
    this.serviceForm?.reset();
    this.eventForm?.reset();
    this.selectedEvent = undefined;
    this.isFirstTime = true;
  }

  stepOneValid(): boolean {
    return !!this.serviceForm.get('service')?.valid && !!this.serviceForm.get('serviceTypeId')?.valid;
  }

  cancelAddOrEditEvent(): void {
    this.selectedEvent = undefined;
    this.step = 2;
  }

  isFormsValids(): boolean {
    if (this.eventForm && this.activatorForm) {
      return this.flowEventService.isFormsValids(this.eventForm, this.activatorForm)
    } else return false
  }

  isServiceFormValid() {
    // Obtener nombres de todos los controles excepto los excluidos
    const controlsToValidate = Object.keys(this.serviceForm.controls)
      .filter(controlName => !['flow', 'eventTypes'].includes(controlName));

    // Verificar validez de cada control relevante
    return controlsToValidate.every(controlName => {
      const control = this.serviceForm.get(controlName);
      return control ? control.valid : true;
    });
  }

  initForms(): void {
    this.serviceForm = this.fb.group({
      service: ['', [Validators.required]],
      serviceTypeId: ['', [Validators.required]],
      startDate: [null],
      endDate: [null],
      forwarderId: [null],
      provedorId: [null], // Mantener por compatibilidad, pero usar provedorIds
      provedorIds: [[]], // Array de IDs de suppliers
      expeditorId: [null],
      destinationContactId: [null],
      origenContactId: [null],
      origin: [null],
      destination: [null],
      frequency: [null],
      totalPlanned: [null, [Validators.min(1), Validators.max(this.totalPlannedMax), Validators.pattern(/^[1-9]\d*$/)]],
      integrate: [false],
      technicalSpecifications: [null],
      flow: [null, [Validators.required]], // Objeto de flujo principal
      eventTypes: this.fb.array([]), // Lista de eventos relativas al Flujo principal
      servicePricing:this.fb.group({
        //nuevos datos
        // cotizacion total
        totalQuantity: [null],
        totalUnit: [null],
        totalPrice: [null],
        totalCost: [null],
        totalDiscount: [null],
        totalSaleVat: [null],
        totalPurchaseVat: [null],
        //cotizacion unitaria
        unitQuantity: [null],
        unit: [null],
        unitPrice: [null],
        unitCost: [null],
        unitDiscount: [null],
        currencyId: [1],
        unitPurchaseVat: [null],
        unitSaleVat: [null],
      }),
      customOperations: [null],
    });

    if (this.flowsList && this.flowsList.length > 1) this.serviceForm.get('serviceTypeId')?.setValue(this.flowsList[0].id);

    this.serviceForm.get('serviceTypeId')!.valueChanges
      .subscribe(value => {
        const selectedServiceType = this.serviceTypes
          .find(st => st.id === value)

        const selectedFlow = selectedServiceType?.flow?.id
          ? this.flowsList.find(f => f.id === selectedServiceType.flow.id)!
          : this.flowsList[0]
        // Inicializar el valor del select de Flujos

        this.serviceForm.get('flow')?.setValue(selectedFlow);

        this.setEvents(selectedFlow);
      })
  }

  async postService(): Promise<Service | undefined> {
    // Obtener supplierIds del formulario (puede ser array vacío o no existir)
    const supplierIds = this.serviceForm.value.provedorIds || [];
    // Si hay supplierIds, usarlos; si no, mantener compatibilidad con supplierId único
    const supplierIdValue = supplierIds.length > 0 ? undefined : this.serviceForm.value.provedorId;
    
    const servicePayload: any = {
      projectId: this.nzData.project.id,
      name: this.serviceForm.value.service,
      typeId: this.serviceForm.value.serviceTypeId,
      startDatePlan: this.serviceForm.value.startDate?.toISOString(),
      endDatePlan: this.serviceForm.value.endDate?.toISOString(),
      forwarderId: this.serviceForm.value.forwarderId,
      supplierId: supplierIdValue, // Mantener por compatibilidad
      supplierIds: supplierIds.length > 0 ? supplierIds : undefined, // Array de IDs (puede estar vacío o no existir)
      shipperId: this.serviceForm.value.expeditorId,
      consigneeId: this.serviceForm.value.destinationContactId,
      originContactId: this.serviceForm.value.originContactId,
      destinationContactId: this.serviceForm.value.destinationContactId,
      originId: this.serviceForm.value.origin?.id,
      destinationId: this.serviceForm.value.destination?.id,
      technicalSpecifications: this.serviceForm.value.technicalSpecifications,
      integrate: !!this.serviceForm.value.integrate,
      frequency: this.serviceForm.value.frequency,
      totalPlanned: this.serviceForm.value.totalPlanned ? Number(this.serviceForm.value.totalPlanned) : null,
      //nuevos datos
      servicePricingPayload:{
        // cotizacion total
        totalQuantity: this.serviceForm.value.servicePricing.totalQuantity,
        totalUnit: this.serviceForm.value.servicePricing.totalUnit,
        totalPrice: this.serviceForm.value.servicePricing.totalPrice,
        totalCost: this.serviceForm.value.servicePricing.totalCost,
        totalDiscount: this.serviceForm.value.servicePricing.totalDiscount,
        totalSaleVat: this.serviceForm.value.servicePricing.totalSaleVat,
        totalPurchaseVat: this.serviceForm.value.servicePricing.totalPurchaseVat,
        //cotizacion unitaria
        unitQuantity: this.serviceForm.value.servicePricing.unitQuantity,
        unit: this.serviceForm.value.servicePricing.unit,
        unitPrice: this.serviceForm.value.servicePricing.unitPrice,
        unitCost: this.serviceForm.value.servicePricing.unitCost,
        unitDiscount: this.serviceForm.value.servicePricing.unitDiscount,

        unitPurchaseVat: this.serviceForm.value.servicePricing.unitPurchaseVat,
        unitSaleVat: this.serviceForm.value.servicePricing.unitSaleVat,
        currencyId: this.serviceForm.value.servicePricing.currencyId
      },
      customOperations: this.serviceForm.value.customOperations

    }
    const cleanPayload = this.deepClean(servicePayload)

    try {
      const response = await lastValueFrom(
        this.stServiceService.createService(cleanPayload)
      );
      return response.data; // Retorna la respuesta exitosa
    } catch (e) {
      console.error(e);
      return undefined; // Retorna undefined en caso de error
    }
  }

  /** Metodo para crear las esntidades de Flow, EventType y EventTrigger **/
  async createEventsEntities(serviceId: number): Promise<boolean> {
    try {

      // Primero se crea el Flow relacionado al Service creado
      const flowPayload = {
        name: this.serviceForm.value.flow.name,
        serviceId: serviceId,
        isActive: true,
        isNative: false,
      }

      const flowRsp = await lastValueFrom(
        this.flowEventService.createFlow(flowPayload)
      );

      // Luego se recorren todos lo EventTypes asociados al FLow para crear cada entidad en el
      for (const et of this.serviceForm.value.flow.eventTypes) {

        // Si es uno de los eventos especiales de inicio/fin se ignora y pasa al siguiente evento
        // if (et.name === 'Orden finalizada' || et.name === 'Orden creada') continue;

        // Se configuran el eventClassId y el eventTriggerTypeId del EventType
        let eventClassId = 0;
        let eventTriggerTypeId = 0;
        if (et.activator?.name === 'Manual') {
          eventClassId = 1; // (Manual)
          eventTriggerTypeId = 2 // (Manual)
        } else {
          eventClassId = 2; // (Automatico)
          if (et.activator?.name === 'Fecha Mojito') {
            eventTriggerTypeId = 3 // (Fecha Mojito)
          } else if (et.activator?.name === 'Subida documento') {
            eventTriggerTypeId = 1 // (Subida documento)
          } else {
            eventTriggerTypeId = 4 // (Validacion documento)
          }
        }

        // Luego se crea el payload del POST del EventType
        const eventTypePayload = {
          name: et.name,
          isActive: true,
          sequence: et.sequence,
          flowId: flowRsp.data.id,
          eventClassId: eventClassId,
          color: et.color,
          icon: et.icon,
          locationId: et.activator?.location?.id,
          mojitoDynamicField: et.activator?.mojitoDynamicField?.enum,
        }

        // Se crea en BD el EventType con los datos dados
        const eventTypeRsp = await lastValueFrom(
          this.flowEventService.createEventType(deepClean(eventTypePayload))
        );

        // const date = eventTriggerTypeId === 3 ? (typeof(et.activator?.value) === 'object' ? et.activator?.value.toISOString() : et.activator?.value) : ''
        const eventTriggerPayload = {
          name: et.activator?.name || 'Manual',
          integrationField: eventTriggerTypeId === 3 ? et.activator?.value : '-',
          eventTypeId: eventTypeRsp.data.id,
          documentUploaded: eventTriggerTypeId === 1,
          documentValidation: eventTriggerTypeId === 4,
          documentTypeId: (eventTriggerTypeId === 1 || eventTriggerTypeId === 4) ? et.activator.value : null,
          eventTriggerTypeId,
          // expirationDate: date,
        }

        const eventTriggerRsp = await lastValueFrom(
          this.flowEventService.createEventTrigger(deepClean(eventTriggerPayload))
        );

        const etId = eventTriggerRsp.data.id;
      }

      return true;

    } catch (e) {
      console.error(e);
      return false;
    }
  }

  getServicecTypeIcon(id: number): string {
    return this.serviceTypes.find(st => st.id === id)?.icon || 'close'
  }

  getProviderById(id: number): Partner | undefined {
    return this.providers.find(p => p.id === id);
  }

  getTotalPlannedErrorTip(): string {
    const totalPlannedControl = this.serviceForm?.get('totalPlanned');
    return totalPlannedControl?.hasError('max')
      ? `El maximo permitido es ${this.totalPlannedMaxLabel}`
      : 'Debe ser un entero positivo';
  }

}
